import random from "random";
import type { PatternPart } from "~/types/pattern";

export type PatternGeneratorConfig = {
  rootNote: string;
  scale: number[]; // progression degrees
  bpm: number;
  noteLength: string; // e.g. "4n", "8n"
  timeSignatureBeats: number; // e.g. 4 for 4/4
  space: number; // 0-100 (higher = more rests/space)
  dissonance: number; // 0-100
  spread: number; // 0-37 semitones (max jump distance from current note)
  range: number; // 1-37 semitones from root (pitch span, up to 3 octaves)
  chromatic: string[]; // all available notes
  tonicGravity?: number; // -12 to +12: bias toward root (positive) or away (negative), 0 = no bias
};

/**
 * Apply tonic gravity weighting to a candidate array.
 * Returns a weighted array where candidates are repeated based on their
 * proximity to the root note (semitone 0).
 *
 * Positive gravity: notes closer to root get more weight (resolve toward tonic)
 * Negative gravity: notes farther from root get more weight (push away from tonic)
 */
function applyTonicGravity(candidates: number[], gravity: number, range: number): number[] {
  if (gravity === 0 || candidates.length <= 1) {
    return candidates;
  }

  // Normalize gravity to 0-1 strength (input is -12 to +12)
  const strength = Math.abs(gravity) / 12;
  const pullToward = gravity > 0; // true = toward root, false = away

  const weighted: number[] = [];

  for (const semitone of candidates) {
    // Distance from root as fraction of range (0 = at root, 1 = at max range)
    const distanceFraction = range > 0 ? semitone / range : 0;

    // Calculate weight: higher weight = more likely to be picked
    // Using exponential curve: weight goes from 1 (min) to maxWeight
    // maxWeight scales with strength: at strength=1, maxWeight=8
    const maxWeight = 1 + Math.round(strength * 7); // 1 to 8

    let weight: number;
    if (pullToward) {
      // Positive gravity: closer to root = higher weight
      weight = Math.max(1, Math.round(maxWeight * (1 - distanceFraction)));
    } else {
      // Negative gravity: farther from root = higher weight
      weight = Math.max(1, Math.round(maxWeight * distanceFraction));
    }

    for (let i = 0; i < weight; i++) {
      weighted.push(semitone);
    }
  }

  return weighted;
}

/**
 * Calculate how many notes fit in one bar
 */
function getNotesPerBar(noteLength: string, beatsPerBar: number): number {
  const notesPerBeat: Record<string, number> = {
    "1n": 0.25, // whole note = 4 beats, so 0.25 per beat
    "2n": 0.5,  // half note = 2 beats
    "4n": 1,    // quarter note = 1 beat
    "8n": 2,    // eighth note = 0.5 beats
    "16n": 4,   // sixteenth note = 0.25 beats
  };

  const notesPerBeatValue = notesPerBeat[noteLength] || 1;
  return Math.floor(beatsPerBar * notesPerBeatValue);
}

/**
 * Generate a single bar (part) of notes
 * Returns an array of PatternNotes (note + duration)
 * @param config - Generation configuration
 * @param fixedSemitone - Optional fixed semitone for 0% spread (drone note)
 */
export function generatePatternPart(
  config: PatternGeneratorConfig,
  fixedSemitone?: number
): PatternPart {
  console.log("[PATTERN] Generating pattern part with config:", {
    rootNote: config.rootNote,
    noteLength: config.noteLength,
    space: config.space,
    spread: config.spread,
    range: config.range,
    dissonance: config.dissonance,
    fixedSemitone,
  });

  const notesInBar = getNotesPerBar(config.noteLength, config.timeSignatureBeats);
  console.log(`[PATTERN] Notes per bar: ${notesInBar}`);
  const part: PatternPart = [];

  // Find root note index in chromatic scale
  const rootIndex = config.chromatic.findIndex((n) => n === config.rootNote);
  if (rootIndex === -1) {
    throw new Error(`Root note "${config.rootNote}" not found in chromatic scale`);
  }
  console.log(`[PATTERN] Root note "${config.rootNote}" at index ${rootIndex}`);


  // Track the last semitone position (0 to range) for spread calculation
  // If fixedSemitone is provided (0% spread), use it as the starting point
  let lastSemitone: number | null = fixedSemitone !== undefined ? fixedSemitone : null;
  const baseOctave = 2; // Start at octave 2

  // Generate notes for this bar
  for (let i = 0; i < notesInBar; i++) {
    // Apply space - higher value = more rests
    const shouldBeRest = random.float() < (config.space / 100);

    if (shouldBeRest) {
      part.push({
        note: null, // rest
        duration: config.noteLength,
      });
      // Only reset lastSemitone if spread > 0 (allows movement after rests)
      // At 0% spread, keep the same note even after rests
      if (config.spread > 0) {
        lastSemitone = null;
      }
      continue;
    }

    let semitonePosition: number; // Position within the range (0 to config.range)

    // Calculate available semitone positions within range
    const availableSemitones: number[] = [];
    for (let semitone = 0; semitone <= config.range; semitone++) {
      availableSemitones.push(semitone);
    }

    // Apply spread - controls how far we can jump from last note (in semitones)
    if (lastSemitone !== null && config.spread === 0) {
      // 0 spread = stay on same note (no movement)
      semitonePosition = lastSemitone;
    } else if (lastSemitone !== null && config.spread < config.range) {
      // Spread is in semitones - clamp the range to stay within boundaries
      // Calculate the allowed range [minSemitone, maxSemitone] clamped to [0, config.range]
      const minSemitone = Math.max(0, lastSemitone - config.spread);
      const maxSemitone = Math.min(config.range, lastSemitone + config.spread);

      // Filter to semitones within the clamped spread distance
      const spreadSemitones = availableSemitones.filter(semitone => {
        return semitone >= minSemitone && semitone <= maxSemitone;
      });

      // Filter to scale notes
      const scaleSemitones = spreadSemitones.filter(semitone => {
        const semitoneWithinOctave = semitone % config.chromatic.length;
        return config.scale.includes(semitoneWithinOctave);
      });

      // Pick from scale notes, or all spread candidates if none in scale
      let finalCandidates = scaleSemitones.length > 0 ? scaleSemitones : spreadSemitones;

      // Apply tonic gravity weighting
      const gravity = config.tonicGravity ?? 0;
      if (gravity !== 0) {
        finalCandidates = applyTonicGravity(finalCandidates, gravity, config.range);
      }

      semitonePosition = finalCandidates[random.int(0, finalCandidates.length - 1)];
    } else {
      // First note or spread >= range - pick any note from scale within full range
      const scaleSemitones = availableSemitones.filter(semitone => {
        const semitoneWithinOctave = semitone % config.chromatic.length;
        return config.scale.includes(semitoneWithinOctave);
      });

      let finalCandidates = scaleSemitones.length > 0 ? scaleSemitones : availableSemitones;

      // Apply tonic gravity weighting
      const gravity = config.tonicGravity ?? 0;
      if (gravity !== 0) {
        finalCandidates = applyTonicGravity(finalCandidates, gravity, config.range);
      }

      semitonePosition = finalCandidates[random.int(0, finalCandidates.length - 1)];
    }

    // Apply dissonance - occasionally pick a note outside the scale
    // At 0% spread, dissonance should not change the semitone position (stay on same note)
    if (config.spread > 0 && random.float() < (config.dissonance / 100)) {
      // Pick a random semitone from available range that's not in scale
      const nonScaleSemitones = availableSemitones.filter(semitone => {
        const semitoneWithinOctave = semitone % config.chromatic.length;
        return !config.scale.includes(semitoneWithinOctave);
      });

      if (nonScaleSemitones.length > 0) {
        semitonePosition = nonScaleSemitones[random.int(0, nonScaleSemitones.length - 1)];
      }
    }

    // Convert semitone position to note + octave
    const octaveOffset = Math.floor(semitonePosition / config.chromatic.length);
    const noteIndex = (rootIndex + semitonePosition) % config.chromatic.length;
    const noteName = config.chromatic[noteIndex];
    const octave = baseOctave + octaveOffset;
    const finalNote = `${noteName}${octave}`;

    lastSemitone = semitonePosition;

    part.push({
      note: finalNote,
      duration: config.noteLength,
    });
  }

  console.log(`[PATTERN] Generated part with ${part.length} notes/rests:`,
    part.map(n => n.note || 'rest').join(', '));
  return part;
}

/**
 * Generate all parts needed for a pattern
 * @param partsNeeded - Array of part names like ["A", "B", "A", "B"]
 * @param config - Generation configuration
 * @returns Object with generated parts
 */
export function generatePatternParts(
  partsNeeded: string[],
  config: PatternGeneratorConfig
): Record<string, PatternPart> {
  console.log("[PATTERN] generatePatternParts called");
  console.log("[PATTERN] Parts needed:", partsNeeded);

  // ALWAYS generate all 4 parts (A, B, C, D) to allow seamless structure switching
  // This prevents timing issues when switching from e.g. ABCB to ABCD
  const allParts = ["A", "B", "C", "D"];
  console.log("[PATTERN] Generating all parts:", allParts);

  const parts: Record<string, PatternPart> = {};

  // At 0% spread, pick one drone note to use across all parts
  let fixedSemitone: number | undefined = undefined;
  if (config.spread === 0) {
    // Pick a random note from the scale within the range
    const availableSemitones: number[] = [];
    for (let semitone = 0; semitone <= config.range; semitone++) {
      availableSemitones.push(semitone);
    }

    // Filter to scale notes
    const scaleSemitones = availableSemitones.filter(semitone => {
      const semitoneWithinOctave = semitone % config.chromatic.length;
      return config.scale.includes(semitoneWithinOctave);
    });

    // Pick one semitone for all parts
    fixedSemitone = scaleSemitones.length > 0
      ? scaleSemitones[random.int(0, scaleSemitones.length - 1)]
      : availableSemitones[random.int(0, availableSemitones.length - 1)];
  }

  for (const partName of allParts) {
    console.log(`[PATTERN] Generating part "${partName}"`);
    parts[partName] = generatePatternPart(config, fixedSemitone);
  }

  console.log("[PATTERN] All parts generated successfully:", Object.keys(parts));
  return parts;
}
